/**
 * FSAReconciliationPage — Conciliacion Fiado SA (Transfer ↔ Banco)
 * Split panel: tabla resumen anual (izq) + detalle drill-down (der)
 */

import { useState } from 'react';
import { RefreshCw, Save, AlertTriangle, ArrowLeft, ArrowRightLeft, Landmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { Can } from '@/components/auth/Can';
import { ROUTES, UNE_PERM } from '@/constants';
import { useFSAReconciliation } from '@/features/reconciliation/hooks';
import { CollectorSummaryTable } from '@/features/reconciliation/components/CollectorSummaryTable';
import { FSADetailPanel } from '@/features/reconciliation/components/FSADetailPanel';
import { RegisterReconciliationModal } from '@/features/reconciliation/components/RegisterReconciliationModal';
import {
  FSA_SUMMARY_COLUMNS,
  FSA_RECONCILIATION_CONFIG,
} from '@/types/fsa-reconciliation.types';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2021 }, (_, i) => ({
  value: String(currentYear + 1 - i),
  label: String(currentYear + 1 - i),
}));

const FSA_LEGEND_ITEMS = [
  { label: 'OK', color: 'bg-green-500' },
  { label: 'Salio de FSA no llego a FMX', color: 'bg-red-500' },
  { label: 'Llego a FMX no salio de FSA', color: 'bg-rose-500' },
  { label: 'Sin banco', color: 'bg-amber-500' },
  { label: 'Sin cuadrar', color: 'bg-slate-500' },
];

export function FSAReconciliationPage() {
  const navigate = useNavigate();
  const {
    year,
    setYear,
    drillDown,
    resumen,
    monthData,
    drillDownItems,
    isLoading,
    hasData,
    isRegistering,
    runReconciliation,
    handleCellClick,
    closeDrillDown,
    register,
  } = useFSAReconciliation();

  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const handleRegister = (notes: string) => {
    if (!resumen) return;
    const discrepancias =
      resumen.totals.salioSinEntrar +
      resumen.totals.entroSinSalir +
      resumen.totals.sinBanco;
    register({
      year,
      month: null,
      totals: resumen.totals,
      status: discrepancias > 0 ? 'CON_DISCREPANCIAS' : 'LIMPIO',
      ...(notes ? { notes } : {}),
    });
    setShowRegisterModal(false);
  };

  // Header content for the summary table
  const fsaHeaderContent = (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <ArrowRightLeft size={12} className="text-emerald-400" />
      <span className="text-emerald-400 font-medium">Transfer</span>
      <span>↔</span>
      <Landmark size={12} className="text-cyan-400" />
      <span className="text-cyan-400 font-medium">Banco</span>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.RECONCILIATION_DASHBOARD)}
            className="mt-1 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Volver"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-xl font-semibold text-foreground">Conciliacion Fiado SA</h1>
              <HelpButton pageKey="reconciliation-fsa" />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {FSA_RECONCILIATION_CONFIG.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            options={YEAR_OPTIONS}
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            className="w-24"
          />
          <Can permission={UNE_PERM.RECONCILIATION_RUN}>
            <Button onClick={runReconciliation} disabled={isLoading}>
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              {hasData ? 'Actualizar' : 'Conciliar'}
            </Button>
            {hasData && (
              <Button variant="outline" onClick={() => setShowRegisterModal(true)}>
                <Save size={16} /> Registrar
              </Button>
            )}
          </Can>
        </div>
      </div>

      {/* Main content */}
      {isLoading && !hasData ? (
        <div className="space-y-4">
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={400} />
        </div>
      ) : !hasData ? (
        <Card>
          <div className="flex flex-col items-center py-16 text-center">
            <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Sin datos de conciliacion</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Selecciona un ano y haz click en &quot;Conciliar&quot; para ejecutar la conciliacion.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2">
          {/* Panel izquierdo — Tabla resumen */}
          <Card className="flex flex-col overflow-hidden">
            <CollectorSummaryTable
              monthData={monthData as Record<string, Record<string, unknown>> | null}
              onCellClick={handleCellClick}
              selectedMonth={drillDown?.month ?? null}
              selectedStatus={drillDown?.statusKey ?? null}
              columns={FSA_SUMMARY_COLUMNS}
              headerContent={fsaHeaderContent}
              legendItems={FSA_LEGEND_ITEMS}
            />
          </Card>

          {/* Panel derecho — Detalle */}
          <Card className="flex flex-col overflow-hidden">
            {drillDown ? (
              <FSADetailPanel
                selection={drillDown}
                items={drillDownItems as Record<string, unknown>[]}
                onClose={closeDrillDown}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Selecciona una celda para ver el detalle
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Register modal */}
      {showRegisterModal && resumen && (
        <RegisterReconciliationModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onConfirm={handleRegister}
          resumen={resumen as unknown as { totals: Record<string, number> }}
          isRegistering={isRegistering}
          summaryLabel="Transfer ↔ Banco (Transferencias declaradas vs Estado de Cuenta)"
          summaryItems={[
            { key: 'ok', label: 'OK', color: 'text-emerald-600' },
            { key: 'salioSinEntrar', label: 'Salio de FSA no llego a FMX', color: 'text-red-600', isError: true },
            { key: 'entroSinSalir', label: 'Llego a FMX no salio de FSA', color: 'text-rose-600', isError: true },
            { key: 'sinBanco', label: 'Sin banco', color: 'text-amber-600', isError: true },
            { key: 'sinCuadrar', label: 'Sin cuadrar', color: 'text-slate-500' },
          ]}
        />
      )}
    </div>
  );
}
